/**
 * The duel connection.
 *
 * Replaces the prototype's BroadcastChannel with a WebSocket into the
 * DuelRoom Durable Object. Everything the prototype could not do lives here:
 * duels between real devices, reconnection after a dropped socket, and an
 * immediate, correct answer when a room code does not exist.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_DECK, type DeckId, type Entry } from "../../shared/vocab";
import {
  CLOSE,
  PROTOCOL_VERSION,
  type ClientMsg,
  type ServerMsg,
  type Side,
  type WordCount,
} from "../../shared/protocol";
import { createDuel } from "./api";
import { activeRoom, playerId, setActiveRoom } from "./storage";

export type DuelPhase =
  | "idle"
  | "creating"
  | "waiting"
  | "joining"
  | "playing"
  | "finished"
  | "over";

export type DuelState = {
  phase: DuelPhase;
  code: string;
  side: Side | null;
  deck: Entry[];
  deckId: DeckId;
  bidx: number;
  oppName: string;
  myScore: number;
  oppScore: number;
  oppDone: number;
  myFinished: boolean;
  oppFinished: boolean;
  myMs: number;
  oppMs: number;
  /** False while the opponent is disconnected. The duel keeps going. */
  oppPresent: boolean;
  oppLeft: boolean;
  error: string;
};

const INITIAL: DuelState = {
  phase: "idle",
  code: "",
  side: null,
  deck: [],
  deckId: DEFAULT_DECK,
  bidx: 0,
  oppName: "",
  myScore: 0,
  oppScore: 0,
  oppDone: 0,
  myFinished: false,
  oppFinished: false,
  myMs: 0,
  oppMs: 0,
  oppPresent: true,
  oppLeft: false,
  error: "",
};

/**
 * Generous, because a dropped socket no longer costs the duel: the room holds
 * the player's place for two minutes. Roughly 45 seconds of retries fits well
 * inside that.
 */
const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_MAX_DELAY_MS = 5000;

export function useDuel() {
  const [state, setState] = useState<DuelState>(INITIAL);

  // The socket callbacks fire outside React's render cycle, so they read state
  // from this mirror rather than from a captured closure.
  const stateRef = useRef(state);
  stateRef.current = state;

  const wsRef = useRef<WebSocket | null>(null);
  const codeRef = useRef("");
  const nameRef = useRef("Player");
  /** This player's own prompt language, announced on every (re)connection. */
  const deckRef = useRef<DeckId>(DEFAULT_DECK);
  const startedAt = useRef(0);
  const retries = useRef(0);
  const closingOnPurpose = useRef(false);
  const reconnectTimer = useRef<number | undefined>(undefined);

  const post = useCallback((msg: ClientMsg) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const dropSocket = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try {
        ws.close();
      } catch {
        // already closed
      }
    }
    clearTimeout(reconnectTimer.current);
  }, []);

  const handle = useCallback((msg: ServerMsg) => {
    switch (msg.t) {
      case "room-missing":
        closingOnPurpose.current = true;
        setActiveRoom(null);
        setState((s) => ({ ...s, phase: "idle", side: null, error: "No duel found for that code." }));
        return;

      case "room-full":
        closingOnPurpose.current = true;
        setActiveRoom(null);
        setState((s) => ({ ...s, phase: "idle", side: null, error: "That duel already has two players." }));
        return;

      case "version-mismatch":
        closingOnPurpose.current = true;
        setActiveRoom(null);
        setState((s) => ({ ...s, phase: "idle", side: null, error: "The app was updated. Reload the page to duel." }));
        return;

      case "waiting":
        setState((s) => ({ ...s, phase: "waiting", code: msg.code, side: msg.side, error: "" }));
        return;

      case "start":
        // Elapsed time is measured locally from here. Using the DO's clock
        // would fold the client's clock skew into the result.
        startedAt.current = performance.now();
        retries.current = 0;
        setState((s) => ({
          ...s,
          phase: "playing",
          deck: msg.deck,
          deckId: msg.deckId,
          oppName: msg.opponent,
          bidx: 0,
          myScore: 0,
          oppScore: 0,
          oppDone: 0,
          myFinished: false,
          oppFinished: false,
          myMs: 0,
          oppMs: 0,
          oppPresent: true,
          oppLeft: false,
          error: "",
        }));
        return;

      case "opp-away":
        setState((s) => ({ ...s, oppPresent: false }));
        return;

      case "opp-back":
        setState((s) => ({ ...s, oppPresent: true }));
        return;

      case "opp-progress":
        setState((s) => ({ ...s, oppDone: msg.done, oppScore: msg.score }));
        return;

      case "opp-finish":
        setState((s) => ({
          ...s,
          oppFinished: true,
          oppScore: msg.score,
          oppMs: msg.ms,
          oppDone: s.deck.length,
          phase: s.myFinished ? "over" : s.phase,
        }));
        return;

      case "opp-left":
        // Only ever sent once the grace period has run out.
        setState((s) =>
          s.phase === "playing" || s.phase === "finished" || s.phase === "over"
            ? { ...s, oppLeft: true, oppFinished: true, oppPresent: false, phase: "over" }
            : { ...s, phase: "idle", side: null, error: "The host left." },
        );
        return;

      case "state": {
        const snap = msg.snapshot;
        // Degraded path: the local clock was lost with the socket, so fall
        // back to the DO's start stamp. Skew only affects a reconnection.
        startedAt.current = performance.now() - Math.max(0, Date.now() - snap.startAt);
        retries.current = 0;
        setState((s) => ({
          ...s,
          phase: snap.phase === "over" ? "over" : snap.you.finished ? "finished" : "playing",
          code: snap.code,
          side: snap.side,
          deck: snap.deck,
          deckId: snap.deckId,
          bidx: snap.you.done,
          oppName: snap.opponent.name || "Opponent",
          myScore: snap.you.score,
          oppScore: snap.opponent.score,
          oppDone: snap.opponent.done,
          myFinished: snap.you.finished,
          oppFinished: snap.opponent.finished,
          myMs: snap.you.ms,
          oppMs: snap.opponent.ms,
          oppPresent: snap.opponent.present,
          oppLeft: snap.oppLeft,
          error: "",
        }));
        return;
      }
    }
  }, []);

  const connect = useCallback(
    (code: string) => {
      dropSocket();
      closingOnPurpose.current = false;
      codeRef.current = code;

      const scheme = location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(`${scheme}//${location.host}/api/duel/${code}/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            t: "hello",
            v: PROTOCOL_VERSION,
            playerId: playerId(),
            name: nameRef.current,
            deckId: deckRef.current,
          } satisfies ClientMsg),
        );
      };

      ws.onmessage = (ev) => {
        try {
          handle(JSON.parse(String(ev.data)) as ServerMsg);
        } catch {
          // malformed frame — ignore rather than tear down a live duel
        }
      };

      ws.onclose = (ev) => {
        if (wsRef.current !== ws) return; // superseded by a newer socket
        wsRef.current = null;
        if (closingOnPurpose.current) return;

        const terminal =
          ev.code === CLOSE.ROOM_FULL ||
          ev.code === CLOSE.EXPIRED ||
          ev.code === CLOSE.VERSION_MISMATCH;
        if (terminal) return; // `handle` already set the message

        const phase = stateRef.current.phase;
        const midDuel = phase === "playing" || phase === "finished";

        if (midDuel && retries.current < MAX_RECONNECT_ATTEMPTS) {
          const attempt = ++retries.current;
          setState((s) => ({ ...s, error: "Connection lost — reconnecting…" }));
          reconnectTimer.current = setTimeout(
            () => connect(codeRef.current),
            Math.min(1000 * attempt, RECONNECT_MAX_DELAY_MS),
          ) as unknown as number;
          return;
        }

        if (midDuel) {
          setState((s) => ({
            ...s,
            error: "Connection lost. Reload the page to rejoin the duel.",
          }));
          return;
        }

        if (phase === "waiting" || phase === "joining" || phase === "creating") {
          setState((s) => ({ ...s, phase: "idle", side: null, error: "Couldn't reach the duel. Try again." }));
        }
      };
    },
    [dropSocket, handle],
  );

  /* ── actions ──────────────────────────────────────────────────────── */

  const create = useCallback(
    async (deckId: DeckId, wordCount: WordCount, name: string) => {
      nameRef.current = name;
      deckRef.current = deckId;
      setState((s) => ({ ...s, phase: "creating", side: "host", deckId, error: "" }));
      try {
        const { code } = await createDuel(playerId(), wordCount);
        setActiveRoom({ code, name, deckId });
        setState((s) => ({ ...s, code }));
        connect(code);
      } catch {
        setState((s) => ({ ...s, phase: "idle", side: null, error: "Couldn't create a duel. Try again." }));
      }
    },
    [connect],
  );

  const join = useCallback(
    (code: string, name: string, deckId: DeckId) => {
      nameRef.current = name;
      deckRef.current = deckId;
      setActiveRoom({ code, name, deckId });
      setState((s) => ({ ...s, phase: "joining", code, side: "guest", deckId, error: "" }));
      connect(code);
    },
    [connect],
  );

  const leave = useCallback(() => {
    closingOnPurpose.current = true;
    setActiveRoom(null);
    post({ t: "leave" });
    dropSocket();
    setState(INITIAL);
  }, [dropSocket, post]);

  /** Score updates the moment the answer is checked, as the design specifies. */
  const scoreAnswer = useCallback((ok: boolean) => {
    if (ok) setState((s) => ({ ...s, myScore: s.myScore + 1 }));
  }, []);

  /** Move to the next word, reporting progress — or finishing the duel. */
  const advance = useCallback(() => {
    const s = stateRef.current;
    const done = s.bidx + 1;
    post({ t: "progress", done, score: s.myScore });

    if (done >= s.deck.length) {
      const ms = Math.round(performance.now() - startedAt.current);
      post({ t: "finish", score: s.myScore, ms });
      setState((prev) => ({
        ...prev,
        bidx: done,
        myFinished: true,
        myMs: ms,
        phase: prev.oppFinished ? "over" : "finished",
      }));
      return;
    }
    setState((prev) => ({ ...prev, bidx: done }));
  }, [post]);

  const rematch = useCallback(() => post({ t: "rematch" }), [post]);

  const clearError = useCallback(() => setState((s) => ({ ...s, error: "" })), []);

  /* ── teardown ─────────────────────────────────────────────────────── */

  /**
   * A reload used to drop you out of the duel: the room held your place for two
   * minutes, but the page came back with no idea which room it had been in.
   * The tab remembers, so it reconnects itself and the Durable Object replies
   * with the full state.
   */
  useEffect(() => {
    const room = activeRoom();
    if (!room) return;
    nameRef.current = room.name || "Player";
    deckRef.current = room.deckId;
    connect(room.code);
    setState((s) => ({ ...s, phase: "joining", code: room.code }));
    // Mount only: later connections go through create/join.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // pagehide fires where beforeunload does not, notably on mobile Safari.
    // The DO treats the socket closing as the real leave signal either way;
    // this only makes the opponent's screen update sooner.
    const bye = () => post({ t: "leave" });
    window.addEventListener("pagehide", bye);
    return () => {
      window.removeEventListener("pagehide", bye);
      closingOnPurpose.current = true;
      dropSocket();
    };
  }, [dropSocket, post]);

  return { duel: state, create, join, leave, scoreAnswer, advance, rematch, clearError };
}
